import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { BatchMonitoringService } from './batch-monitoring.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-batch-monitoring',
    templateUrl: './batch-monitoring.component.html',
    styleUrls: ['./batch-monitoring.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BatchMonitoringComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  filterParams: any = {};

  /** Global runtime params (legacy name; flat hydrated params). */
  globalService: any = {};
  GENERAL_CONFIG: any = {};

  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Component specific properties
  modalTitle = "Request Input";
  monitoringData: any = {};
  monitoringDataCount = 0;
  showJsonLoader = false;
  entityList: any[] = [];

  constructor(
    private _snackBar: MatSnackBar,
    private batchMonitoringService: BatchMonitoringService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 &&
          this.GENERAL_CONFIG && this.globalService) {
        this.setupEntityData();
        this.fetchMonitoringData();
      }
    } catch (e) {
      console.error('Error in BatchMonitoring init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    // For batchMonitoring, we use REQUEST_ID, STATUS, and CREATED_DATE instead of standard entity fields
    this.entityList = [
      {key: "Request ID", value: this.rowData.REQUEST_ID || 'N/A'},
      {key: "Status", value: this.rowData.STATUS || 'N/A'},
      {key: "Created Date", value: this.rowData.CREATED_DATE || 'N/A'}
    ];
  }

  fetchMonitoringData() {
    this.showJsonLoader = true;

    const params = {
      request_id: this.rowData.REQUEST_ID,
      ist_tkn: this.rowData.IST_REQ_TKN
    };

    this.batchMonitoringService.getMonitoringData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        try {
          const tempMonitoringData = safeJsonInput<any>(response.jsonObject.clob_data, null, 'batch-monitoring.parse1');

          if (!Array.isArray(tempMonitoringData)) {
            Object.keys(tempMonitoringData).forEach(key => {
              const value = tempMonitoringData[key];
              if (typeof value === 'string' && (value.includes("{") || value.includes("["))) {
                try {
                  tempMonitoringData[key] = safeJsonInput<any>(value, null, 'batch-monitoring.parse2');
                } catch (e) {
                  console.warn('Failed to parse JSON for key:', key);
                }
              }
            });
          }

          this.monitoringData = this.objToJsonView(tempMonitoringData);
          this.monitoringDataCount = Object.keys(tempMonitoringData).length;
          this.showJsonLoader = false;
        } catch (e) {
          console.error('Error processing monitoring data:', e);
          this.showJsonLoader = false;
          this._snackBar.open('Error processing request data', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
      },
      (error: any) => {
        console.error('Error fetching monitoring data:', error);
        this.showJsonLoader = false;
        this._snackBar.open('Error fetching request data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  // Convert object to formatted JSON view (similar to ObjectDiff.objToJsonView)
  objToJsonView(obj: any): string {
    try {
      // Convert arrays to objects with numeric keys to match AngularJS behavior
      const convertedObj = this.convertArraysToObjects(obj);
      return this.formatJsonWithoutQuotes(convertedObj, 0);
    } catch (e) {
      console.error('Error converting object to JSON view:', e);
      return 'Error displaying data';
    }
  }

  // Format JSON without quotes around keys to match AngularJS ObjectDiff.objToJsonView behavior
  private formatJsonWithoutQuotes(obj: any, indent: number): string {
    const spaces = '  '.repeat(indent);

    if (obj === null) {
      return 'null';
    }

    if (typeof obj === 'string') {
      return `"${obj}"`;
    }

    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return obj.toString();
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) {
        return '[]';
      }
      const items = obj.map(item => `${spaces}  ${this.formatJsonWithoutQuotes(item, indent + 1)}`);
      return `[\n${items.join(',\n')}\n${spaces}]`;
    }

    if (typeof obj === 'object') {
      const keys = Object.keys(obj);
      if (keys.length === 0) {
        return '{}';
      }
      const items = keys.map(key => {
        const value = this.formatJsonWithoutQuotes(obj[key], indent + 1);
        return `${spaces}  ${key}: ${value}`;
      });
      return `{\n${items.join(',\n')}\n${spaces}}`;
    }

    return String(obj);
  }

  // Convert arrays to objects with numeric keys to match AngularJS ObjectDiff.objToJsonView behavior
  private convertArraysToObjects(obj: any): any {
    if (Array.isArray(obj)) {
      const result: any = {};
      obj.forEach((item, index) => {
        result[index] = this.convertArraysToObjects(item);
      });
      return result;
    } else if (obj !== null && typeof obj === 'object') {
      const result: any = {};
      Object.keys(obj).forEach(key => {
        result[key] = this.convertArraysToObjects(obj[key]);
      });
      return result;
    }
    return obj;
  }

  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

}
